import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { PlusOutlined } from '@ant-design/icons'
import { PageHeader, Button } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { deviceRecordConstants } from 'locale/deviceRecords'
import useRoleAccess from '../../../../components/AccessControl/useRoleAccess'
import { UserRole } from '@lmc/ts-common'
import { DeviceRecordList } from './components/DeviceRecordList'
import DeviceRecordModal from './components/DeviceRecordModal'
import { useDeviceRecordsList } from './hooks/useDeviceRecordsList'
import { DeviceRecord } from './types'

export const ResidentDeviceRecordsScreen: React.FC = () => {
  const { t } = useTranslation(NAMESPACE.DEVICE_RECORDS)
  const { id: residentId } = useParams<{ id: string }>()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedDeviceRecord, setSelectedDeviceRecord] = useState<
    DeviceRecord | undefined
  >(undefined)

  const {
    deviceRecords,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useDeviceRecordsList({
    residentId: residentId || '',
    limit: 50
  })

  const hasAddAccess = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin, UserRole.seniorCarer],
    [
      {
        i: 'create',
        this: 'DeviceRecord'
      }
    ]
  )

  const handleAddClick = () => {
    setSelectedDeviceRecord(undefined)
    setIsModalVisible(true)
  }

  const handleEdit = (deviceRecord: DeviceRecord) => {
    setSelectedDeviceRecord(deviceRecord)
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedDeviceRecord(undefined)
  }

  return (
    <>
      <PageHeader title={t(deviceRecordConstants.DEVICE_RECORDS)} sticky>
        {hasAddAccess && (
          <Button variant="primary" onClick={handleAddClick}>
            <PlusOutlined />
            {t(deviceRecordConstants.ADD_DEVICE_RECORD)}
          </Button>
        )}
      </PageHeader>

      <DeviceRecordList
        deviceRecords={deviceRecords}
        isLoading={isLoading}
        onEdit={handleEdit}
        onRefetch={refetch}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />

      <DeviceRecordModal
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        onSuccess={handleCloseModal}
        deviceRecord={selectedDeviceRecord}
        residentId={residentId || ''}
      />
    </>
  )
}
